
import React from 'react';
import { DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { useContactPhoto } from './hooks/useContactPhoto';
import { PhotoUrlInput } from './components/PhotoUrlInput';
import { PhotoFileUpload } from './components/PhotoFileUpload';

interface Contact {
  id: string;
  full_name: string | null;
  title: string | null;
  work_email: string | null;
  linkedin_profile?: string | null;
  phone_number?: string | null;
  key_contact: boolean;
  avatar_url?: string | null;
}

interface ContactPhotoUploadProps {
  contact: Contact;
  fundId: string;
  onComplete: (updatedContact: Contact) => void;
}

export const ContactPhotoUpload = ({ contact, fundId, onComplete }: ContactPhotoUploadProps) => {
  const {
    isUploading,
    avatarUrlInput,
    setAvatarUrlInput,
    handleFileUpload,
    handleUrlUpdate,
    removePhoto
  } = useContactPhoto(contact, fundId, onComplete);

  return (
    <DialogContent className="sm:max-w-[425px]">
      <DialogHeader>
        <DialogTitle>Update Contact Photo</DialogTitle>
        <DialogDescription>
          Upload a new photo for this contact or provide a photo URL.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <PhotoUrlInput
          value={avatarUrlInput}
          onChange={setAvatarUrlInput}
          onUpdate={handleUrlUpdate}
          isLoading={isUploading}
        />

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Or
            </span>
          </div>
        </div>

        <PhotoFileUpload
          onFileSelect={handleFileUpload}
          isLoading={isUploading}
        />

        {contact.avatar_url && (
          <Button
            variant="destructive"
            onClick={removePhoto}
            disabled={isUploading}
            className="w-full"
          >
            {isUploading ? (
              <>
                <Spinner size="sm" className="mr-2" ariaLabel="Removing" />
                Removing...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Remove Photo
              </>
            )}
          </Button>
        )}
      </div>
    </DialogContent>
  );
};
